import { useMemo } from "react";
import { Options } from "./populateMultiCheckboxList.hook";
import { OccupationEntity } from "../../../occupation/domain/occupation.entity";

export interface SmartFilterOptions {
  adults: boolean;
  babies: boolean;
  children: boolean;
  juniors: boolean;
  seniors: boolean;
}

function countActiveFilters(filters: SmartFilterOptions): number {
  return Object.values(filters).filter(Boolean).length;
}

function filterOccupations(
  occupations: OccupationEntity[],
  filters: SmartFilterOptions
): OccupationEntity[] {
  const activeCount = countActiveFilters(filters);

  // Only adults
  if (filters.adults && activeCount === 1) {
    return occupations.filter(
      occ => occ.adults > 0 && !occ.seniors && !occ.babies && !occ.children && !occ.juniors
    );
  }

  // Only seniors
  if (filters.seniors && activeCount === 1) {
    return occupations.filter(
      occ => occ.seniors > 0 && !occ.adults && !occ.babies && !occ.children && !occ.juniors
    );
  }

  // Adults and seniors only
  if (filters.adults && filters.seniors && activeCount === 2) {
    return occupations.filter(
      occ => occ.adults > 0 && occ.seniors > 0 && !occ.babies && !occ.children && !occ.juniors
    );
  }

  // Filter minors
  let filtered = occupations;

  if (filters.babies && !filters.children && !filters.juniors) {
    filtered = filtered.filter(occ => !occ.children && !occ.juniors);
  } else if (filters.babies && filters.children && !filters.juniors) {
    filtered = filtered.filter(occ => !occ.juniors);
  } else if (filters.babies && filters.juniors && !filters.children) {
    filtered = filtered.filter(occ => !occ.children);
  } else if (filters.children && !filters.babies && !filters.juniors) {
    filtered = filtered.filter(occ => !occ.babies && !occ.juniors);
  } else if (filters.children && filters.juniors && !filters.babies) {
    filtered = filtered.filter(occ => !occ.babies);
  } else if (filters.juniors && !filters.babies && !filters.children) {
    filtered = filtered.filter(occ => !occ.babies && !occ.children);
  }

  // Require adults if any minors are selected
  if (!filters.adults && (filters.juniors || filters.babies || filters.children)) {
    filtered = filtered.filter(
      occ => occ.adults > 0 && (occ.babies > 0 || occ.juniors > 0 || occ.children > 0)
    );
  }

  return filtered;
}

function mapOccupationsToOptions(occupations: OccupationEntity[]) {
  return occupations.map(it => ({
    ticker: it.ticker,
    translations: it.translationsList.map(translation => ({
      locale: translation.locale,
      name: translation.name,
    })),
  }));
}

export function useFilterOccupationsBySelectedGuest(
  originalOptions: Options,
  smartFilterApplied: SmartFilterOptions,
  occupations?: OccupationEntity[]
): Options {
  const hasSmartFilterAppliedValue = useMemo(
    () => Object.values(smartFilterApplied).some(Boolean),
    [smartFilterApplied]
  );

  return useMemo(() => {
    if (!hasSmartFilterAppliedValue || !originalOptions || !occupations) {
      return originalOptions;
    }

    const filteredOccupations = filterOccupations(occupations, smartFilterApplied);

    return {
      ...originalOptions,
      occupations: mapOccupationsToOptions(filteredOccupations),
    };
  }, [hasSmartFilterAppliedValue, occupations, originalOptions, smartFilterApplied]);
}
